import { getElements } from '../Utility/Dom'

const STORAGE_KEY = 'mauticFormData'

class MauticForm {
  private backButton: HTMLElement | null = null
  private containers: [] | NodeListOf<HTMLElement> = []
  private form: HTMLElement | null = null
  private messageContainer: HTMLElement | null = null

  constructor() {
    this.containers = getElements('mautic-form')
  }

  public init(): void {
    this.containers.forEach((container: HTMLElement): void => {
      this.form = container.querySelector('.mauticform-innerform')
      this.messageContainer = container.querySelector('#mauticform_register_message')
      this.backButton = container.querySelector('#sy_backbutton')

      if (!this.form || !this.messageContainer) {
        return
      }

      this.hideBackButton()
      this.setReturnUrl(container)
      this.prefillEmailFromUrl(container)
      this.showMauticError(container)
      this.addEvents(container)
    })
  }

  private addEvents(container: HTMLElement): void {
    this.observeMessage()

    const formElement = container.querySelector<HTMLFormElement>('form')
    formElement?.addEventListener('submit', (): void => {
      this.saveFormData(container)
    })

    this.backButton?.addEventListener('click', (): void => {
      this.showForm()
    })

    const selectAllButton = container.querySelector('#sy_alltopics') ?? container.querySelector('#select-all')
    selectAllButton?.addEventListener('click', (): void => {
      this.toggleAllCheckboxes(container, true)
    })

    const resetButton = container.querySelector('#reset-filters')
    resetButton?.addEventListener('click', (): void => {
      this.toggleAllCheckboxes(container, false)
    })
  }

  private hideBackButton(): void {
    if (this.backButton) this.backButton.hidden = true
  }

  private hideForm(): void {
    if (this.form) this.form.hidden = true
    if (this.backButton) this.backButton.hidden = false

    const selectAllButton = this.form?.closest('section')?.querySelector('#sy_alltopics, #select-all')
    if (selectAllButton instanceof HTMLElement) selectAllButton.hidden = true
  }

  private observeMessage(): void {
    const observer = new MutationObserver((mutations: MutationRecord[]): void => {
      if (mutations[0]?.addedNodes.length > 0) {
        this.hideForm()
      }
    })

    observer.observe(this.messageContainer!, {
      characterData: true,
      childList: true,
      subtree: true,
    })
  }

  private prefillEmailFromUrl(container: HTMLElement): void {
    const email = new URLSearchParams(window.location.search).get('email')

    if (!email) {
      return
    }

    const emailInput = container.querySelector<HTMLInputElement>('input[type="email"]')

    if (emailInput) {
      emailInput.value = email
    }
  }

  private restoreFormData(container: HTMLElement): void {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return
    }

    const data: Record<string, string | string[]> = JSON.parse(raw)
    window.sessionStorage.removeItem(STORAGE_KEY)

    for (const [name, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        container.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`).forEach((checkbox: HTMLInputElement): void => {
          checkbox.checked = value.includes(checkbox.value)
        })
      } else {
        const input = container.querySelector<HTMLInputElement>(`[name="${name}"]`)

        if (input) {
          input.value = value
        }
      }
    }
  }

  private saveFormData(container: HTMLElement): void {
    const data: Record<string, string | string[]> = {}
    const inputs = container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, textarea, select')

    inputs.forEach((input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void => {
      if (!input.name || input.type === 'hidden' || input.type === 'submit') {
        return
      }

      if (input instanceof HTMLInputElement && input.type === 'checkbox') {
        if (!input.checked) {
          return
        }

        const existing = data[input.name]

        if (Array.isArray(existing)) {
          existing.push(input.value)
        } else {
          data[input.name] = [input.value]
        }
      } else {
        data[input.name] = input.value
      }
    })

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  private setReturnUrl(container: HTMLElement): void {
    const returnInput = container.querySelector<HTMLInputElement>('input[name="mauticform[return]"]')

    if (returnInput) {
      returnInput.value = window.location.href.split('?')[0]
    }
  }

  private showForm(): void {
    if (this.form) this.form.hidden = false
    if (this.backButton) this.backButton.hidden = true
    if (this.messageContainer) this.messageContainer.innerHTML = ''

    const selectAllButton = this.form?.closest('section')?.querySelector('#sy_alltopics, #select-all')
    if (selectAllButton instanceof HTMLElement) selectAllButton.hidden = false
  }

  private showMauticError(container: HTMLElement): void {
    const params = new URLSearchParams(window.location.search)
    const error = params.get('mauticError')

    if (!error || !this.messageContainer) {
      return
    }

    this.messageContainer.innerHTML = error
    this.restoreFormData(container)
    container.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  private toggleAllCheckboxes(container: HTMLElement, checked: boolean): void {
    const checkboxes = container.querySelectorAll<HTMLInputElement>('#mauticform_register_thema .mauticform-checkboxgrp-checkbox')

    if (checked && checkboxes.length > 0 && checkboxes[0].checked) {
      checkboxes.forEach((checkbox: HTMLInputElement): void => {
        checkbox.checked = false
      })
      return
    }

    checkboxes.forEach((checkbox: HTMLInputElement): void => {
      checkbox.checked = checked
    })
  }
}

const mauticForm = new MauticForm()
mauticForm.init()
